import React, { useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { FaEnvelope, FaLock, FaGoogle, FaArrowLeft, FaCheckCircle } from "react-icons/fa";
import { GoogleLogin } from "@react-oauth/google";
import "../../styles/AuthForm.css";

export default function LoginForm() {
  // Estados para login normal
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Estados para verificación
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationEmail, setVerificationEmail] = useState("");
  const [verificationUserId, setVerificationUserId] = useState(null);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const navigate = useNavigate();
  const API_URL = "/backend";

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/login.php`,
        { email: email.trim(), password },
        { withCredentials: true, headers: { "Content-Type": "application/json" } }
      );

      if (res.data.success) {
        // Usuario ya verificado, login normal
        localStorage.setItem("userData", JSON.stringify(res.data.user));
        localStorage.setItem("userToken", res.data.token);
        window.dispatchEvent(new Event('storage'));
        navigate("/account");
      } else if (res.data.needsVerification) {
        // Usuario necesita verificación - mostrar pantalla
        setVerificationEmail(res.data.email);
        setVerificationUserId(res.data.user_id);
        setShowVerification(true);
      } else {
        setError(res.data.error || "Credenciales incorrectas");
      }
    } catch (err) {
      console.error(err);
      setError("Error de conexión con el servidor");
    } finally {
      setLoading(false);
    }
  };

  // Función para verificar código
  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError("El código debe tener 6 dígitos");
      return;
    }

    setVerificationLoading(true);
    setError("");

    try {
      // Usar user_id en lugar de email para mayor precisión
      const res = await axios.post(`${API_URL}/verificar_codigo.php`,
        { user_id: verificationUserId, codigo: verificationCode },
        { headers: { "Content-Type": "application/json" } }
      );

      if (res.data.success) {
        setVerificationSuccess(true);
        
        // Hacer login automático después de verificar
        setTimeout(async () => {
          try {
            const loginRes = await axios.post(`${API_URL}/login.php`,
              { email: verificationEmail, password },
              { withCredentials: true, headers: { "Content-Type": "application/json" } }
            );
            
            if (loginRes.data.success) {
              localStorage.setItem("userData", JSON.stringify(loginRes.data.user));
              localStorage.setItem("userToken", loginRes.data.token);
              window.dispatchEvent(new Event('storage'));
              navigate("/account");
            } else {
              navigate("/login");
            }
          } catch (error) {
            console.error("Error en login automático:", error);
            navigate("/login");
          }
        }, 2000);
      } else {
        setError(res.data.error || "Código incorrecto");
      }
    } catch (err) {
      console.error(err);
      setError("Error al verificar código");
    } finally {
      setVerificationLoading(false);
    }
  };

  // Función para reenviar código
  const handleResendCode = async () => {
    setResendDisabled(true);
    setResendTimer(60);
    
    // Contador regresivo
    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    try {
      await axios.post(`${API_URL}/enviar_codigo.php`,
        { user_id: verificationUserId, email: verificationEmail },
        { headers: { "Content-Type": "application/json" } }
      );
    } catch (error) {
      console.error("Error enviando código:", error);
    }
  };

  const handleGoogleLoginSuccess = async (credentialResponse) => {
    setError("");
    setLoading(true);

    try {
      const credential = credentialResponse.credential;
      const res = await axios.post(`${API_URL}/google-auth.php`,
        { credential },
        { headers: { "Content-Type": "application/json" }, withCredentials: true }
      );

      if (res.data.success) {
        localStorage.setItem("userData", JSON.stringify(res.data.user));
        localStorage.setItem("userToken", res.data.token);
        window.dispatchEvent(new Event('storage'));
        navigate("/account");
      } else {
        setError(res.data.error || "Error al iniciar sesión con Google");
      }
    } catch (err) {
      console.error(err);
      setError("Error procesando inicio de sesión con Google");
    } finally {
      setLoading(false);
    }
  };

  // Si está mostrando la verificación
  if (showVerification) {
    return (
      <div className="auth-form-container">
        <div className="auth-form-header">
          <div className="auth-form-logo">
            <span className="auth-form-logo-icon">⚡</span>
            <span className="auth-form-logo-text">Reanimeitor</span>
          </div>
          <h2 className="auth-form-title">Verifica tu email</h2>
          <p className="auth-form-subtitle">
            Tu cuenta aún no está verificada. Hemos enviado un código a <strong>{verificationEmail}</strong>
          </p>
        </div>

        {verificationSuccess ? (
          <div className="verification-success">
            <div className="success-animation">
              <FaCheckCircle className="success-icon" />
            </div>
            <h3>¡Email verificado!</h3>
            <p>Redirigiendo a tu cuenta...</p>
          </div>
        ) : (
          <>
            {error && <div className="form-error-message">{error}</div>}

            <div className="verification-instructions">
              <p>Revisa tu bandeja de entrada y spam. El código expira en 15 minutos.</p>
            </div>

            <div className="verification-code-input">
              <input
                type="text"
                maxLength="6"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => {
                  setVerificationCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6));
                  setError("");
                }}
                className="code-input"
                autoFocus
              />
            </div>

            <button 
              className="auth-submit-btn" 
              onClick={handleVerifyCode}
              disabled={verificationLoading || verificationCode.length !== 6}
            >
              {verificationLoading ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  Verificando...
                </div>
              ) : "Verificar código"}
            </button>

            <div className="verification-footer">
              <button 
                className="resend-btn"
                onClick={handleResendCode}
                disabled={resendDisabled}
              >
                {resendDisabled ? `Reenviar en ${resendTimer}s` : "Reenviar código"}
              </button>
              
              <button 
                className="back-btn"
                onClick={() => {
                  setShowVerification(false);
                  setVerificationCode("");
                  setError("");
                }}
              >
                <FaArrowLeft /> Volver
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // Login normal
  return (
    <div className="auth-form-container">
      <div className="auth-form-header">
        <div className="auth-form-logo">
          <span className="auth-form-logo-icon">⚡</span>
          <span className="auth-form-logo-text">Reanimeitor</span>
        </div>
        <h2 className="auth-form-title">Iniciar Sesión</h2>
        <p className="auth-form-subtitle">Bienvenido de vuelta a la energía</p>
      </div>

      {error && <div className="form-error-message">{error}</div>}

      <form className="auth-form" onSubmit={handleLogin}>
        <div className="form-group">
          <div className="input-with-icon">
            <FaEnvelope className="input-icon" />
            <input 
              type="email" 
              className="form-input" 
              placeholder="tu@email.com"
              required 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
            />
          </div>
        </div>

        <div className="form-group">
          <div className="input-with-icon">
            <FaLock className="input-icon" />
            <input 
              type="password" 
              className="form-input" 
              placeholder="••••••••"
              required 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
            />
          </div>
        </div>

        {/* ========== ENLACE "OLVIDÉ MI CONTRASEÑA" ========== */}
        <div className="forgot-password-container">
          <Link to="/forgot-password" className="forgot-password-link">
            ¿Olvidaste tu contraseña?
          </Link>
        </div>

        <button type="submit" className="auth-submit-btn" disabled={loading}>
          {loading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
              Iniciando Sesión...
            </div>
          ) : "Iniciar Sesión"}
        </button>

        <div className="auth-divider"><span>o</span></div>

        {/* Google Login */}
        <GoogleLogin
          onSuccess={handleGoogleLoginSuccess}
          onError={() => setError("Error con inicio de sesión de Google")}
        />
      </form>

      <div className="auth-footer">
        <p>
          ¿No tienes cuenta? <Link to="/register" className="auth-link">Regístrate aquí</Link>
        </p>
      </div>
    </div>
  );
}